import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { oktaAuth } from '../../auth/oktaConfig';
import { useAuth } from '../../auth/AuthContext';

export function LoginCallbackPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const hasHandled = useRef(false);
  const [status, setStatus] = useState('Signing in...');
  const [debugInfo, setDebugInfo] = useState<string | null>(null);

  useEffect(() => {
    if (hasHandled.current) return;
    hasHandled.current = true;

    const handleCallback = async () => {
      try {
        setStatus('Processing authentication...');
        // console.log('[LoginCallback] Starting OAuth callback handling');
        // console.log('[LoginCallback] URL:', window.location.href);

        // Check for explicit error from Okta
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
        const error = urlParams.get('error') || hashParams.get('error');
        
        if (error) {
          const desc = urlParams.get('error_description') || hashParams.get('error_description') || '';
          // console.error('[LoginCallback] OAuth error:', error, desc);
          setStatus('Authentication error');
          setDebugInfo(`OAuth error: ${error}\n${desc}`);
          setTimeout(() => navigate('/login?reason=access_denied', { replace: true }), 5000);
          return;
        }

        // Check if URL has authorization code (PKCE flow)
        const hasCode = urlParams.has('code');
        
        if (hasCode) {
          // Parse tokens directly from URL using token.parseFromUrl()
          // This does the PKCE code exchange without triggering any redirects
          setStatus('Exchanging authorization code...');
          try {
            const { tokens } = await oktaAuth.token.parseFromUrl();
            // console.log('[LoginCallback] parseFromUrl success:', {
            //   hasAccessToken: !!tokens.accessToken,
            //   hasIdToken: !!tokens.idToken,
            // });
            // Store tokens in the token manager
            oktaAuth.tokenManager.setTokens(tokens);
            // console.log('[LoginCallback] Tokens stored in manager');
          } catch (parseError: any) {
            // console.error('[LoginCallback] parseFromUrl failed:', parseError?.message);
            // SDK may have already processed during init - check if tokens exist
            const existingToken = await oktaAuth.tokenManager.get('accessToken');
            if (!existingToken || !('accessToken' in existingToken)) {
              setStatus('Token exchange failed');
              setDebugInfo(`parseFromUrl error: ${parseError?.message}\nThis may indicate CORS is not configured for this domain in Okta Trusted Origins.`);
              setTimeout(() => navigate('/login?reason=no_code', { replace: true }), 8000);
              return;
            }
            // console.log('[LoginCallback] Tokens found in manager despite parseFromUrl error');
          }
        } else {
          // No code in URL - check if SDK already has tokens (auto-processed during init)
          setStatus('Verifying tokens...');
          const existingToken = await oktaAuth.tokenManager.get('accessToken');
          const isAuth = await oktaAuth.isAuthenticated();
          if (!existingToken && !isAuth) {
            setStatus('No authorization code received');
            setDebugInfo(`URL has no 'code' parameter and no existing tokens.\nURL: ${window.location.href}`);
            setTimeout(() => navigate('/login?reason=no_code', { replace: true }), 8000);
            return;
          }
          // console.log('[LoginCallback] No code in URL but tokens exist (SDK auto-processed)');
        }

        // Wait for token manager to settle
        await new Promise(resolve => setTimeout(resolve, 100));

        // Refresh auth context state (identity service + roles)
        setStatus('Loading user profile...');
        //console.log('[LoginCallback] Calling refreshAuth...');
        await auth.refreshAuth();
        
        //console.log('[LoginCallback] refreshAuth complete, navigating to /admin');
        setStatus('Redirecting to dashboard...');
        
        // Small delay to ensure React state commits before navigation
        await new Promise(resolve => setTimeout(resolve, 50));
        navigate('/admin', { replace: true });
      } catch (error: any) {
        const msg = error?.message || String(error);
        console.error('[LoginCallback] Callback error:', msg);
        setStatus('Authentication error');
        setDebugInfo(`Error: ${msg}`);
        setTimeout(() => navigate('/login?reason=okta_unavailable', { replace: true }), 8000);
      }
    };

    handleCallback();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(180deg, #0a1628 0%, #1a237e 30%, #4a148c 70%, #880e4f 100%)',
      color: 'white',
      fontSize: '1.1rem',
      gap: '16px',
    }}>
      <div>{status}</div>
      {debugInfo && (
        <pre style={{ 
          fontSize: '0.75rem', 
          background: 'rgba(0,0,0,0.5)', 
          padding: '12px', 
          borderRadius: '8px',
          maxWidth: '600px',
          whiteSpace: 'pre-wrap',
        }}>
          {debugInfo}
        </pre>
      )}
    </div>
  );
}
